import React, { useState } from 'react';
import { History, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

/**
 * Change history for one event (Oct 2026, migration 035). Every insert/update
 * delete on `events` is logged by a DB trigger: who, when, old → new per field.
 * Collapsed by default; loads only when opened.
 */
const LABELS = {
  date: 'Date', end_date: 'End date', name: 'Event name', stage: 'Status', type: 'Type',
  guests: 'Guests', venue_id: 'Room', start_time: 'Start', end_time: 'End', setup: 'Setup',
  food_service_time: 'Food time ("none" = Drinks only)', music_only: 'Music booking only',
  services: 'Service lines', notes: 'Notes', menu_notes: 'Menu', setup_requirements: 'Setup requirements',
  dietary_requirements: 'Dietary notes', dietary_veg: 'Vegetarian', dietary_vegan: 'Vegan',
  dietary_coeliac: 'Coeliac', dietary_allergens: 'Allergens', dietary_na: 'Dietary N/A',
  price_per_head: 'Price per head', package_total: 'Package total', room_hire: 'Room hire',
  client_name: 'Client', client_phone: 'Phone', client_email: 'Email', deposit_paid: 'Deposit',
};

const show = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string') return v.length > 140 ? `${v.slice(0, 140)}…` : v;
  const s = JSON.stringify(v);
  return s.length > 140 ? `${s.slice(0, 140)}…` : s;
};

export default function EventHistory({ eventId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');

  async function load() {
    setErr('');
    const { data, error } = await supabase
      .from('event_history')
      .select('id, action, changed_at, changed_by_email, changed_fields, changes')
      .eq('event_id', eventId)
      .order('changed_at', { ascending: false })
      .limit(100);
    if (error) setErr(error.message); else setRows(data || []);
  }

  if (!eventId) return null;
  return (
    <div className="mt-4 rounded-lg border border-slate-200">
      <button type="button" onClick={() => { const o = !open; setOpen(o); if (o) load(); }}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <History size={14} /> Change history
      </button>
      {open && (
        <div className="px-3 pb-3 text-xs">
          {err && <p className="text-red-600">Couldn't load history: {err}</p>}
          {!err && rows === null && <p className="text-slate-400">Loading…</p>}
          {rows && rows.length === 0 && <p className="text-slate-400">No changes recorded yet (history started 30 Oct 2026).</p>}
          {rows && rows.map((r) => (
            <div key={r.id} className="border-t border-slate-100 py-1.5">
              <div className="text-slate-500">
                {new Date(r.changed_at).toLocaleString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {' · '}{r.changed_by_email || 'system'}
                {r.action !== 'update' && <b className="ml-1 uppercase">{r.action === 'insert' ? 'created' : 'deleted'}</b>}
              </div>
              {r.action === 'update' && (r.changed_fields || []).map((f) => (
                <div key={f} className="text-slate-800">
                  <b>{LABELS[f] || f}:</b> <span className="text-slate-500 line-through">{show(r.changes?.[f]?.old)}</span> → {show(r.changes?.[f]?.new)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
